import { useState, type FormEvent, type ReactNode } from 'react';
import { getRouteApi, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CloudDownload, Pencil } from 'lucide-react';
import type { ClientDetail, ClientPatchBody } from '@erp/shared';
import { api } from '@/lib/api-client';
import { useAuthUser } from '@/auth/use-auth';
import { Button } from '@/components/ui/button';
import { Input, Label, Textarea } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Badge, Dialog, EmptyState, Skeleton } from '@/components/ui/misc';
import { cn } from '@/lib/utils';
import { clientDetailOptions, errorMessage } from '@/features/clients/clients-queries';
import { ClientStatusDot } from '@/features/clients/ClientsPage';
import { ClientAddressesSection } from '@/features/clients/client-addresses-section';
import { ClientContactsSection } from '@/features/clients/client-contacts-section';
import { ClientEntitiesSection } from '@/features/clients/client-entities-section';
import { ClientUsersSection } from '@/features/clients/client-users-section';
import { ClientTicketsSection } from '@/features/clients/client-tickets-section';
import { ClientContractsSection } from '@/features/billing/client-contracts-section';
import { ClientResourcesSection } from '@/features/resources/client-resources-section';
import { TifluxPullDialog } from '@/features/clients/tiflux-pull-dialog';

const routeApi = getRouteApi('/_authenticated/clients/$clientId');

type TabId = 'info' | 'tickets' | 'contracts' | 'resources';

function DetailSkeleton() {
  return (
    <div className="space-y-4" aria-busy>
      <div className="rounded-lg border bg-card p-4 shadow-xs">
        <Skeleton className="h-3.5 w-40" />
        <Skeleton className="mt-3 h-6 w-72" />
        <Skeleton className="mt-4 h-8 w-56" />
      </div>
      <div className="rounded-lg border bg-card p-4 shadow-xs">
        <Skeleton className="h-4 w-24" />
        <div className="mt-4 grid gap-4 sm:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="space-y-1.5">
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-4 w-32" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="text-[13px] text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-sm break-words">{children}</div>
    </div>
  );
}

export function ClientDetailPage() {
  const { clientId: clientIdParam } = routeApi.useParams();
  const clientId = Number(clientIdParam);
  const queryClient = useQueryClient();
  const detail = useQuery(clientDetailOptions(clientId));
  const [editOpen, setEditOpen] = useState(false);
  const [tab, setTab] = useState<TabId>('info');

  const toggleStatus = useMutation({
    mutationFn: async (status: boolean) => {
      const body: ClientPatchBody = { status };
      const res = await api<ClientDetail>(`/clients/${clientId}`, { method: 'PATCH', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['erp', 'clients'] }),
  });

  if (detail.isPending) return <DetailSkeleton />;
  if (detail.isError) {
    return (
      <div className="rounded-lg border bg-card shadow-xs">
        <EmptyState message={`Erro ao carregar cliente: ${errorMessage(detail.error)}`} />
      </div>
    );
  }
  const client = detail.data;

  const tabs: { id: TabId; label: string; count?: number }[] = [
    { id: 'info', label: 'Informações' },
    { id: 'tickets', label: 'Tickets', count: client.openTicketsCount },
    { id: 'contracts', label: 'Contratos' },
    { id: 'resources', label: 'Recursos' },
  ];

  return (
    <div className="space-y-4">
      {/* Cabeçalho: breadcrumb + nome + switch ativo + abas */}
      <div className="rounded-lg border bg-card shadow-xs">
        <div className="px-4 pt-3">
          <nav className="text-[13px] text-muted-foreground" aria-label="Breadcrumb">
            <Link to="/clients" search={{ q: '', page: 1 }} className="hover:text-foreground">
              Clientes
            </Link>
            <span className="mx-1.5">/</span>
            <span className="text-foreground">{client.name}</span>
          </nav>
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-2">
            <h1 className="max-w-full truncate text-xl leading-7 font-semibold">{client.name}</h1>
            {client.social ? (
              <span className="hidden max-w-72 truncate text-sm text-muted-foreground sm:inline">
                {client.social}
              </span>
            ) : null}
            <ClientStatusDot active={client.status} />
            <div className="ml-auto flex items-center gap-4">
              <label className="flex cursor-pointer items-center gap-2 text-sm">
                Cliente ativo
                <Switch
                  checked={client.status}
                  disabled={toggleStatus.isPending}
                  onCheckedChange={(checked) => toggleStatus.mutate(checked)}
                  aria-label={client.status ? 'Inativar cliente' : 'Ativar cliente'}
                />
              </label>
              <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
                <Pencil className="size-3.5" />
                Editar
              </Button>
            </div>
          </div>
          {toggleStatus.error ? (
            <p className="mt-1 text-sm text-destructive">{errorMessage(toggleStatus.error)}</p>
          ) : null}
          {client.operatedInErp ? <TifluxOperatedBar client={client} /> : null}
        </div>
        <div className="mt-2 flex gap-6 border-b border-divider px-4" role="tablist">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              onClick={() => setTab(t.id)}
              className={cn(
                '-mb-px flex h-10 items-center gap-1.5 border-b-2 px-1 text-sm font-medium transition-colors',
                tab === t.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              )}
            >
              {t.label}
              {t.count !== undefined ? (
                <span
                  className={cn(
                    'rounded-full px-1.5 py-px text-xs tabular-nums',
                    tab === t.id
                      ? 'bg-secondary text-secondary-foreground'
                      : 'bg-accent text-muted-foreground'
                  )}
                >
                  {t.count}
                </span>
              ) : null}
            </button>
          ))}
        </div>
      </div>

      {tab === 'info' ? (
        <>
          {/* Dados */}
          <section className="rounded-lg border bg-card p-4 shadow-xs">
            <div className="mb-3 flex items-center justify-between gap-2">
              <h2 className="text-base font-semibold">Dados</h2>
              <button
                type="button"
                onClick={() => setEditOpen(true)}
                aria-label="Editar dados do cliente"
                className="flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
              >
                <Pencil className="size-4" />
              </button>
            </div>
            <div className="grid gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
              <Field label="Nome fantasia">{client.name}</Field>
              <Field label="Razão social">
                {client.social ?? <span className="text-muted-foreground">—</span>}
              </Field>
              <Field label="CNPJ/CPF">
                {client.socialRevenue ? (
                  <span className="tabular-nums">{client.socialRevenue}</span>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </Field>
              <Field label="Tickets abertos">
                <span className="font-semibold text-primary tabular-nums">
                  {client.openTicketsCount}
                </span>
              </Field>
              <div className="sm:col-span-2">
                <Field label="Anotações">
                  {client.notes ? (
                    <span className="whitespace-pre-wrap">{client.notes}</span>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </Field>
              </div>
            </div>
          </section>

          <div className="grid items-start gap-4 lg:grid-cols-2">
            <ClientAddressesSection client={client} />
            <ClientContactsSection client={client} />
          </div>

          <ClientUsersSection clientId={clientId} />

          <ClientEntitiesSection clientId={clientId} />
        </>
      ) : tab === 'tickets' ? (
        <ClientTicketsSection clientId={clientId} />
      ) : tab === 'contracts' ? (
        <ClientContractsSection clientId={clientId} clientName={client.name} />
      ) : (
        <ClientResourcesSection clientId={clientId} clientName={client.name} />
      )}

      {editOpen ? <EditClientDialog client={client} onClose={() => setEditOpen(false)} /> : null}
    </div>
  );
}

/**
 * Cliente operado no ERP (spec 026): quantos tickets mudaram no TiFlux — o
 * ciclo dos operados detecta e grava a pendência, nada muda sozinho — e, para
 * admin, o "Puxar do TiFlux". A contagem é o mesmo predicado do indicador no
 * detalhe do ticket (FR-005).
 */
function TifluxOperatedBar({ client }: { client: ClientDetail }) {
  const isAdmin = useAuthUser()?.role === 'admin';
  const [open, setOpen] = useState(false);
  const n = client.tifluxPendingTickets;
  return (
    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-md border border-dashed px-3 py-2 text-sm">
      <Badge variant="success" title="Desligado do espelho do TiFlux: os tickets são editados aqui">
        Operado no ERP
      </Badge>
      {n > 0 ? (
        <span className="flex items-center gap-1.5 text-[#D48806]">
          <AlertTriangle className="size-3.5 shrink-0" />
          {n === 1 ? '1 ticket mudou no TiFlux' : `${n} tickets mudaram no TiFlux`}
          {isAdmin ? null : <span className="text-muted-foreground">— um administrador decide o que puxar.</span>}
        </span>
      ) : (
        <span className="text-muted-foreground">Nenhuma mudança do TiFlux pendente.</span>
      )}
      {isAdmin ? (
        <Button variant="outline" size="sm" className="ml-auto" onClick={() => setOpen(true)}>
          <CloudDownload className="size-4" />
          Puxar do TiFlux
        </Button>
      ) : null}
      {open ? <TifluxPullDialog clientId={client.id} clientName={client.name} onClose={() => setOpen(false)} /> : null}
    </div>
  );
}

function EditClientDialog({ client, onClose }: { client: ClientDetail; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(client.name);
  const [social, setSocial] = useState(client.social ?? '');
  const [socialRevenue, setSocialRevenue] = useState(client.socialRevenue ?? '');
  const [notes, setNotes] = useState(client.notes ?? '');

  const save = useMutation({
    mutationFn: async (body: ClientPatchBody) => {
      const res = await api<ClientDetail>(`/clients/${client.id}`, { method: 'PATCH', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['erp', 'clients'] });
      onClose();
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    save.mutate({
      name: name.trim(),
      social: social.trim() || null,
      socialRevenue: socialRevenue.trim() || null,
      notes: notes.trim() || null,
    });
  };

  return (
    <Dialog open onClose={onClose} title="Editar cliente">
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="edit-client-name">
            Nome <span className="text-destructive">*</span>
          </Label>
          <Input
            id="edit-client-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="edit-client-social">Razão social</Label>
          <Input
            id="edit-client-social"
            value={social}
            onChange={(e) => setSocial(e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="edit-client-doc">CNPJ/CPF</Label>
          <Input
            id="edit-client-doc"
            value={socialRevenue}
            onChange={(e) => setSocialRevenue(e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="edit-client-notes">Anotações</Label>
          <Textarea
            id="edit-client-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>
        {save.error ? <p className="text-sm text-destructive">{errorMessage(save.error)}</p> : null}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" size="sm" disabled={save.isPending || !name.trim()}>
            {save.isPending ? 'Salvando…' : 'Salvar'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
